import { hasPermissionToEditFile } from '@/app/actions';
import { ContextMenuType } from '@/app/atoms/contextMenuAtom';
import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import type { CellsImage } from '@/app/gridGL/cells/cellsImages/CellsImage';
import type { Table } from '@/app/gridGL/cells/tables/Table';
import { intersects } from '@/app/gridGL/helpers/intersects';
import { checkResizeDestinationInvalid } from '@/app/gridGL/interaction/pointer/moveInvalid';
import { DOUBLE_CLICK_TIME, MINIMUM_MOVE_POSITION } from '@/app/gridGL/interaction/pointer/pointerUtils';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { IMAGE_BORDER_OFFSET } from '@/app/gridGL/UI/UICellImages';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';
import { isMac } from '@/shared/utils/isMac';
import { Rectangle, type FederatedPointerEvent, type Point } from 'pixi.js';
import { parseDocumentImageUri } from 'quadratic-shared/typesAndSchemasDocuments';

const CORNER_SIZE = 20;

export type ResizeSide = 'right' | 'bottom' | 'both';

interface Resizing {
  image: CellsImage;
  table: Table;
  point: Point;
  side: ResizeSide;
  originalWidth: number;
  originalHeight: number;
  // committed footprint in cells, used to find which cells a resize newly covers
  currentColumns: number;
  currentRows: number;
  end: { column: number; row: number };
  // the pending footprint would spill onto other content, so release must not commit it
  invalid: boolean;
}

export class PointerImages {
  resizing?: Resizing;

  cursor: string | undefined;

  private doubleClickTimeout?: number;
  private lastClickedImage?: CellsImage;

  // raw world position of the first click of a potential double click; used to
  // cancel the double click if the pointer moves too far between clicks
  private lastClickedPoint?: Point;

  // Finds a line that is being hovered.
  private findImage(point: Point): { image: CellsImage; side?: ResizeSide } | undefined {
    const cellsSheet = content.cellsSheets.current;
    if (!cellsSheet) return;
    const images = cellsSheet.getCellsImages();
    if (!images?.length) return;
    for (const image of images) {
      const cornerSize = CORNER_SIZE * pixiApp.viewport.scaled;
      const bounds = image.table.tableBounds;
      const corner = new Rectangle(
        bounds.right - cornerSize,
        bounds.bottom - cornerSize,
        cornerSize + IMAGE_BORDER_OFFSET * 2,
        cornerSize + IMAGE_BORDER_OFFSET * 2
      );
      if (intersects.rectanglePoint(corner, point)) return { image, side: 'both' };
      let right = intersects.rectanglePoint(image.viewRight, point);
      let bottom = intersects.rectanglePoint(image.viewBottom, point);
      if (right && bottom) return { image, side: 'both' };
      if (right) return { image, side: 'right' };
      if (bottom) return { image, side: 'bottom' };
      if (intersects.rectanglePoint(bounds, point)) {
        return { image };
      }
    }
  }

  pointerMove(point: Point): boolean {
    if (!hasPermissionToEditFile(gridSettings.getPermissions())) return false;

    // Don't show resize handles during a move operation
    if (pixiApp.pointer.pointerCellMoving.state === 'move') {
      content.cellImages.activate();
      this.cursor = undefined;
      return false;
    }

    if (this.resizing) {
      this.clearDoubleClick();
      const image = this.resizing.image;
      const end = { column: this.resizing.end.column, row: this.resizing.end.row };

      // ensure we're not into negative space; maintain original end column if
      // not resizing that side
      if (this.resizing.side === 'right' || this.resizing.side === 'both') {
        const column = sheets.sheet.offsets.getColumnFromScreen(point.x);
        end.column = Math.max(column, image.column);
      }

      if (this.resizing.side === 'bottom' || this.resizing.side === 'both') {
        const row = sheets.sheet.offsets.getRowFromScreen(point.y);
        // keep at least one image row below the name row (if shown)
        end.row = Math.max(row, image.row + this.headerRows(this.resizing.table));
      }

      const columns = end.column - image.column + 1;
      const rows = end.row - image.row + 1;
      const screenRectangle = sheets.sheet.getScreenRectangle(image.column, image.row, columns, rows);

      // if not corner, then keep the original width/height
      if (this.resizing.side === 'right') {
        screenRectangle.height = image.table.tableBounds.bottom - screenRectangle.top;
      } else if (this.resizing.side === 'bottom') {
        screenRectangle.width = image.table.tableBounds.right - screenRectangle.left;
      }

      this.resizing.image.temporaryResize(screenRectangle.width, screenRectangle.height);
      this.resizing.table.resize(screenRectangle.width, screenRectangle.height);
      this.resizing.end = { column: end.column, row: end.row };
      this.resizing.invalid = checkResizeDestinationInvalid(
        image.column,
        image.row,
        columns,
        rows,
        this.resizing.currentColumns,
        this.resizing.currentRows
      );
      content.cellImages.setInvalid(this.resizing.invalid);
      events.emit('setDirty', { cellImages: true, cursor: true });
      content.cellsSheets.current?.tables.resizeTable(
        this.resizing.image.column,
        this.resizing.image.row,
        screenRectangle.width,
        screenRectangle.height
      );
      pixiApp.setViewportDirty();
      return true;
    }

    const search = this.findImage(point);
    if (search?.side) {
      // Only show resize cursor if the image is selected
      const selectedTableNames = sheets.sheet.cursor.getSelectedTableNames();
      const tableName = search.image.table.codeCell.name;
      if (selectedTableNames.includes(tableName)) {
        content.cellImages.activate(search.image);
        if (search.side === 'bottom') {
          this.cursor = 'row-resize';
        } else if (search.side === 'right') {
          this.cursor = 'col-resize';
        } else if (search.side === 'both') {
          this.cursor = 'all-scroll';
        }
        return true;
      }
    }
    content.cellImages.activate();
    this.cursor = undefined;
    return false;
  }

  pointerDown(point: Point, event: FederatedPointerEvent): boolean {
    if (!hasPermissionToEditFile(gridSettings.getPermissions())) return false;
    const search = this.findImage(point);
    if (search) {
      const table = content.cellsSheet.tables.getTableIntersects(search.image.pos);
      if (!table) {
        console.error('Table not found in PointerImages.pointerDown');
        return false;
      }

      // Don't handle clicks on the table name area - let PointerTable handle those
      // for context menu and other title bar interactions
      if (table.intersectsTableName(point)) {
        return false;
      }

      const isRightClick = event.button === 2 || (isMac && event.button === 0 && event.ctrlKey);

      // Right-click: open context menu
      if (isRightClick) {
        sheets.sheet.cursor.selectTable(table.codeCell.name, undefined, false, false);
        events.emit('contextMenu', {
          type: ContextMenuType.Table,
          table: table.codeCell,
          world: point,
        });
        return true;
      }

      // Only allow resizing if the image is selected
      const selectedTableNames = sheets.sheet.cursor.getSelectedTableNames();
      const isSelected = selectedTableNames.includes(table.codeCell.name);

      if (search.side && isSelected) {
        // Resizing the image
        this.resizing = {
          point,
          image: search.image,
          side: search.side,
          table,
          originalWidth: table.tableBounds.width,
          originalHeight: table.tableBounds.height,
          currentColumns: table.codeCell.w,
          currentRows: table.codeCell.h,
          end: { column: table.codeCell.x + table.codeCell.w - 1, row: table.codeCell.y + table.codeCell.h - 1 },
          invalid: false,
        };
        content.cellImages.activate(search.image);
        return true;
      }

      // Check for double-click. A double click requires the pointer to stay
      // roughly in place between the two clicks; a small drag that still lands
      // on the same image (easy on large images) should be a single click.
      const movedTooFar =
        !this.lastClickedPoint ||
        Math.abs(this.lastClickedPoint.x - point.x) + Math.abs(this.lastClickedPoint.y - point.y) >
          MINIMUM_MOVE_POSITION / pixiApp.viewport.scale.x;
      if (!movedTooFar && this.doubleClickTimeout && this.lastClickedImage === search.image) {
        window.clearTimeout(this.doubleClickTimeout);
        this.doubleClickTimeout = undefined;
        this.lastClickedImage = undefined;
        this.lastClickedPoint = undefined;

        // Check if the image is a URL-based image (not base64)
        const imageData = search.image.dataUrl;
        const isUrlBasedImage = imageData && !imageData.startsWith('data:');
        const isDriveImage = imageData ? parseDocumentImageUri(imageData) !== null : false;

        if (isDriveImage) {
          // Drive images have no user-editable URL, so double-click reopens the
          // picker to swap in a different document.
          events.emit('insertImageFromDrive', {
            sheetId: search.image.sheetId,
            x: search.image.column,
            y: search.image.row,
          });
        } else if (isUrlBasedImage) {
          // Open edit URL dialog for URL-based images
          events.emit('editImageUrl', {
            sheetId: search.image.sheetId,
            x: search.image.column,
            y: search.image.row,
            currentUrl: imageData,
          });
        } else {
          // Open context menu for base64 images
          events.emit('contextMenu', {
            type: ContextMenuType.Table,
            table: table.codeCell,
            world: point,
          });
        }
        return true;
      }

      // Only allow moving if the image is already selected
      if (isSelected) {
        pixiApp.pointer.pointerCellMoving.tableMove(
          table.codeCell.x,
          table.codeCell.y,
          point,
          table.codeCell.w,
          table.codeCell.h,
          table.codeCell.name
        );
      } else {
        // First click - just select the image, don't initiate move
        sheets.sheet.cursor.selectTable(table.codeCell.name, undefined, false, false);
      }

      // Set up double-click tracking
      this.lastClickedImage = search.image;
      this.lastClickedPoint = point.clone();
      this.doubleClickTimeout = window.setTimeout(() => {
        this.doubleClickTimeout = undefined;
        this.lastClickedImage = undefined;
        this.lastClickedPoint = undefined;
      }, DOUBLE_CLICK_TIME);

      return true;
    }
    return false;
  }

  // Number of rows at the top of an image table's footprint taken by its name
  // row; the chart size sent to core excludes it.
  private headerRows(table: Table): number {
    return table.codeCell.show_name ? 1 : 0;
  }

  // Restores the image and its table to the size they had before the resize
  // started.
  private revertResize(resizing: Resizing): void {
    resizing.table.resize(resizing.originalWidth, resizing.originalHeight);
    resizing.image.temporaryResize(resizing.originalWidth, resizing.originalHeight);
    content.cellImages.setInvalid(false);
    events.emit('setDirty', { cellImages: true, cursor: true });
    content.cellsSheet.tables.resizeTable(
      resizing.image.column,
      resizing.image.row,
      resizing.originalWidth,
      resizing.originalHeight
    );
  }

  pointerUp = (): boolean => {
    if (this.resizing) {
      const resizing = this.resizing;
      this.resizing = undefined;

      // Refuse a footprint that would spill onto other content, matching the
      // red preview shown during the drag.
      if (resizing.invalid) {
        this.revertResize(resizing);
        return true;
      }

      quadraticCore
        .setChartSize(
          resizing.image.sheetId,
          resizing.image.column,
          resizing.image.row,
          resizing.end.column - resizing.image.column + 1,
          resizing.end.row - resizing.image.row + 1 - this.headerRows(resizing.table),
          false
        )
        .then((response) => {
          if (!response || !response.result) {
            this.revertResize(resizing);
          }
          if (response?.error) {
            gridSettings.addGlobalSnackbar?.(response.error, { severity: 'error' });
          }
        });

      return true;
    }
    return false;
  };

  handleEscape(): boolean {
    if (this.resizing) {
      this.revertResize(this.resizing);
      this.resizing = undefined;
      return true;
    }
    return false;
  }

  private clearDoubleClick(): void {
    if (this.doubleClickTimeout) {
      window.clearTimeout(this.doubleClickTimeout);
      this.doubleClickTimeout = undefined;
    }
    this.lastClickedImage = undefined;
    this.lastClickedPoint = undefined;
  }

  destroy(): void {
    this.clearDoubleClick();
  }
}
